import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuthContext } from '@myphx-engineering/auth/client';
import {
    AppShell,
    type NavBarProps,
    useNavBar,
} from '@myphx-engineering/phx-design-system';
import { SystemDegradationBanner } from '@phx/maintenance';
import { PwaOverlays } from '@phx/pwa';

import { useSidebarContext } from '../../contexts/SidebarProvider';
import { useAppSystemDegradationStatus } from '../../hooks/use-app-system-degradation-status';
import { useFlags } from '../../hooks/use-flags';
import { usePatientContext } from '../../hooks/use-patient-context';
import { getAbsoluteRoute } from '../../util';
import { CobrandedFooter } from '../unauth-home/CobrandedFooter';
import { Footer } from '../unauth-home/Footer';
import { MaintenanceBanner } from '../unauth-home/MaintenanceBanner';

import { SignUpButton } from './SignUpButton';

export const AppContent = () => {
    const { isAuthenticated } = useAuthContext();
    const { toggle: toggleSidebar } = useSidebarContext();
    const navigate = useNavigate();
    const { backOverrideState, backButtonDisabled } = useNavBar();
    const { pathname, key } = useLocation();
    const { patient } = usePatientContext();
    const homeUrl = isAuthenticated
        ? getAbsoluteRoute('home')
        : getAbsoluteRoute('root');
    const isHome = pathname === homeUrl;
    const isRoot = pathname === getAbsoluteRoute('root');
    const { cobrandingEnabled } = useFlags();
    const systemDegradationStatus = useAppSystemDegradationStatus();

    const goHome = useMemo(() => {
        if (isHome) return;

        return () => navigate(homeUrl);
    }, [homeUrl, isHome, navigate]);

    const isSimplifiedProviderRoute =
        pathname.includes('/direct/') && pathname.endsWith('/providers');

    const handleBack = useMemo(() => {
        // Explanation here https://github.com/remix-run/history/blob/main/docs/api-reference.md#locationkey
        const isInitialLocation = key === 'default';

        if (isSimplifiedProviderRoute) {
            return backOverrideState.overrideFn || undefined;
        }

        if (backOverrideState.overrideFn) return backOverrideState.overrideFn;
        if (isHome || isInitialLocation) return;

        return () => {
            const historyIndex = window.history.state?.idx ?? 0;
            if (historyIndex > 0) {
                navigate(-1);
            } else {
                navigate(homeUrl);
            }
        };
    }, [
        homeUrl,
        isHome,
        isSimplifiedProviderRoute,
        key,
        backOverrideState.overrideFn,
        navigate,
    ]);

    // 1024px for the landing page, fall back to the default everywhere else
    const maxWidth = isRoot ? '1024px' : undefined;
    const AvatarAdornment = isRoot ? SignUpButton : undefined;

    const logoFileName = cobrandingEnabled
        ? 'partner-logo.webp'
        : 'myprescryptive-logo.png';
    const logoUrl = `/static/img/${logoFileName}`;
    const logoProps: Partial<NavBarProps> = {
        logoUrl,
        logoHeight: cobrandingEnabled ? '36px' : undefined,
        logoWidth: 'auto',
    };

    return (
        <AppShell
            maxWidth={maxWidth}
            navBarProps={{
                ...logoProps,
                onBackButtonClick: handleBack,
                onLogoClick: goHome,
                onAvatarClick: toggleSidebar,
                auth: isAuthenticated,
                username: patient?.fullName,
                hideBackButton: backButtonDisabled,
                AvatarAdornment,
            }}
            // For now, we only show the footer on the unauth home screen, but we will likely show it across the app in the future
            footer={isRoot ? <Footer /> : null}
            footerBg="var(--mantine-color-primary-9)"
            header={
                <>
                    <MaintenanceBanner />
                    <SystemDegradationBanner
                        systemDegradationStatus={systemDegradationStatus}
                    />
                </>
            }
        >
            <Outlet />
            {cobrandingEnabled ? <CobrandedFooter /> : null}
            <PwaOverlays />
        </AppShell>
    );
};
